import { z } from "zod";
import { ProStatus, FREE_STATUS } from "./billing.ts";
import { USER_ROLES } from "./enums.ts";

export const SKINS = ["cream", "pink", "blue", "dark"] as const;
export const Settings = z.strictObject({
  skin: z.enum(SKINS).default("cream"),
  voice: z.enum(["en-US", "en-GB"]).default("en-US"),
  collapsed: z.boolean().default(false),
  /** Số hiệp tập trung mục tiêu mỗi ngày (chip x/N ở menu trái và màn Tập trung). */
  focusRounds: z.int().min(1).max(99).default(4),
});
export type Settings = z.infer<typeof Settings>;
export const StudySlot = z.strictObject({
  from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
export const ProfileInput = z.strictObject({
  expectedRev: z.number().int().nonnegative(),
  displayName: z.string().trim().min(1).max(80),
  goalScore: z.number().int().min(10).max(990).multipleOf(5),
  currentLevel: z.enum(["beginner", "basic", "intermediate", "advanced"]),
  dailyGoalMinutes: z.number().int().min(5).max(720),
  studySlot: StudySlot,
  examDate: z.iso.date().nullable().default(null),
});
export const SettingsInput = z.strictObject({
  displayName: z.string().trim().min(1).max(80).optional(),
  expectedRev: z.number().int().nonnegative(),
  settings: z.strictObject({
    skin: z.enum(SKINS).optional(),
    voice: z.enum(["en-US", "en-GB"]).optional(),
    collapsed: z.boolean().optional(),
    focusRounds: z.int().min(1).max(99).optional(),
  }),
});
export const Account = z.strictObject({
  id: z.uuid(),
  email: z.email(),
  name: z.string().nullable(),
  role: z.enum(USER_ROLES),
  csrfToken: z.string(),
  needsOnboarding: z.boolean(),
  activationRequired: z.boolean().optional(),
  hasData: z.boolean(),
  pro: ProStatus.default(FREE_STATUS),
  profile: z.strictObject({
    rev: z.number().int(),
    displayName: z.string().nullable(),
    goalScore: z.number().nullable(),
    currentLevel: z.string().nullable(),
    dailyGoalMinutes: z.number(),
    studySlot: StudySlot.nullable(),
    examDate: z.string().nullable(),
    settings: Settings,
  }),
});
export type Account = z.infer<typeof Account>;

export interface PracticeSkill {
  key: string;
  label: string;
  icon: string;
  parts: readonly number[];
}
/** Thêm speaking/writing ở đây khi triển khai, cùng cấp và không đổi AppShell. */
export const PRACTICE_SKILLS: readonly PracticeSkill[] = [
  { key: "listening", label: "Luyện nghe", icon: "🎧", parts: [1, 2, 3, 4] },
  { key: "reading", label: "Luyện đọc", icon: "📖", parts: [5, 6, 7] },
];
export function legacyDrillRoute(part?: number): string {
  const skill = PRACTICE_SKILLS.find((s) => part !== undefined && s.parts.includes(part));
  return `/practice/${skill?.key ?? "listening"}${skill && part !== undefined ? `/${part}` : ""}`;
}
